"use client";

import { useEffect, useState } from "react";
import { AltArrowLeftLinear, AltArrowRightLinear } from "solar-icon-set";
import { cn } from "../../lib/utils";
import { Button } from "../Button/Button";
import { Input } from "../Input/Input";

export interface PaginationMeta {
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
}

export interface PaginationLabels {
  /** Formatter for results counter, e.g. "1 - 50 of 67". Receives (start, end, total). */
  resultsCounter?: (start: number, end: number, total: number) => string;
  /** Formatter for "of {totalPages}" in the page indicator. */
  ofTotalPages?: (totalPages: number) => string;
  /** "Page" label used in vertical layout. */
  page?: string;
  /** Formatter for single-page display, e.g. "Page 1". Receives (page). */
  pageWithNumber?: (page: number) => string;
  /** Aria-label for previous page button. */
  previousPage?: string;
  /** Aria-label for next page button. */
  nextPage?: string;
  /** Screen-reader label for page number input. */
  pageNumberInput?: string;
  /** Aria-label for the page input, e.g. "Page number, current page 1 of 5". Receives (activePage, total). */
  pageNumberCurrent?: (activePage: number, total: number) => string;
  /** Title for the page input, e.g. "Enter page number (1-5)". Receives (total). */
  enterPageNumber?: (total: number) => string;
}

const defaultLabels: Required<PaginationLabels> = {
  resultsCounter: (start, end, total) => `${start} - ${end} of ${total}`,
  ofTotalPages: (totalPages) => `of ${totalPages}`,
  page: "Page",
  pageWithNumber: (page) => `Page ${page}`,
  previousPage: "Previous page",
  nextPage: "Next page",
  pageNumberInput: "Page number input",
  pageNumberCurrent: (activePage, total) => `Page number, current page ${activePage} of ${total}`,
  enterPageNumber: (total) => `Enter page number (1-${total})`,
};

export interface PaginationProps {
  /** The total number of pages. */
  total?: number;
  /** The current active page (1-based). */
  page?: number;
  /** The number of items per page. */
  perPage?: number;
  /** The total number of items across all pages (used for results counter like "1 - 20 of 150"). */
  totalItems?: number;
  /** Whether to show the results counter (e.g., "1 - 20 of 150"). */
  showResultsCounter?: boolean;
  /** Whether to show the previous and next arrow buttons. */
  showControls?: boolean;
  /** Whether to display pagination controls vertically. */
  isVertical?: boolean;
  /** The function to call when the page changes. Receives the new page number (1-based). */
  onChange: (page: number) => void;
  /** Override labels for i18n support. All fields are optional and fall back to English defaults. */
  labels?: PaginationLabels;
}

export function formatPaginationResults(meta: PaginationMeta): string {
  const start = (meta.page - 1) * meta.perPage + 1;
  const end = Math.min(meta.page * meta.perPage, meta.total);
  return `${start} - ${end} of ${meta.total}`;
}

interface PageInputProps {
  value: string;
  total: number;
  activePage: number;
  onPageChange: (page: number) => void;
  setInputValue: (value: string) => void;
  labels: Required<PaginationLabels>;
}

const PageInput = ({ value, total, activePage, onPageChange, setInputValue, labels }: PageInputProps) => {
  const inputId = `pagination-page-input-${activePage}`;

  return (
    <div>
      <label htmlFor={inputId} className="sr-only">
        {labels.pageNumberInput}
      </label>
      <Input
        id={inputId}
        value={value}
        variant="bordered"
        aria-label={labels.pageNumberCurrent(activePage, total)}
        aria-valuemin={1}
        aria-valuemax={total}
        aria-valuenow={activePage}
        role="spinbutton"
        placeholder={labels.page}
        title={labels.enterPageNumber(total)}
        classNames={{
          base: "w-10 h-8",
          input: "text-center text-text-body-neutral-onMatch-muted",
          inputWrapper: "w-12 h-8 min-h-8 max-h-8",
        }}
        onChange={(e) => {
          const inputValue = e.target.value;
          // Only allow numbers
          if (/^\d*$/.test(inputValue)) {
            setInputValue(inputValue);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            const page = parseInt(value);
            if (page >= 1 && page <= total) {
              onPageChange(page);
            }
          }
        }}
        onBlur={() => {
          const page = parseInt(value);
          if (page >= 1 && page <= total) {
            onPageChange(page);
          } else {
            setInputValue(activePage.toString());
          }
        }}
      />
    </div>
  );
};

export const Pagination = ({
  total = 1,
  page = 1,
  perPage = 10,
  totalItems,
  showResultsCounter = true,
  showControls = true,
  isVertical = false,
  onChange,
  labels: labelOverrides,
  className,
}: PaginationProps & { className?: string }) => {
  const labels = { ...defaultLabels, ...labelOverrides };
  // Controlled: derive everything from props. Parent owns the page state.
  const activePage = page;
  const totalPages = total;
  const [inputValue, setInputValue] = useState(activePage.toString());

  useEffect(() => {
    setInputValue(activePage.toString());
  }, [activePage]);

  const handlePageChange = (next: number) => {
    onChange(next);
  };

  const handlePrevious = () => {
    if (activePage > 1) {
      onChange(activePage - 1);
    }
  };

  const handleNext = () => {
    if (activePage < totalPages) {
      onChange(activePage + 1);
    }
  };

  const isSinglePage = totalPages === 1;

  if (isSinglePage && showResultsCounter && totalItems) {
    const start = (activePage - 1) * perPage + 1;
    const end = Math.min(activePage * perPage, totalItems);
    return (
      <div className={cn("flex flex-row items-center justify-start text-text-body-neutral-onMatch-muted", className)}>
        <div>
          <p className="text-sm text-text-body-neutral-onMatch-faint">
            {labels.resultsCounter(start, end, totalItems)}
          </p>
        </div>
      </div>
    );
  }

  if (totalItems === 0 || totalItems === undefined) {
    return (
      <div className={cn("flex flex-row items-center justify-start text-text-body-neutral-onMatch-muted", className)}>
        <div>
          <p className="text-sm text-text-body-neutral-onMatch-faint">{labels.pageWithNumber(1)}</p>
        </div>
      </div>
    );
  }

  if (isVertical) {
    return (
      <div
        className={cn("flex flex-col gap-2 pb-2 text-center text-sm text-text-body-neutral-onMatch-muted", className)}
      >
        <div className="flex flex-col items-center gap-2">
          {showControls && (
            <Button
              variant="light"
              isIconOnly
              aria-label={labels.previousPage}
              onClick={handlePrevious}
              disabled={activePage === 1}
            >
              <AltArrowLeftLinear className="size-4 rotate-90 text-icon-neutral-onMatch-usual md:size-6" />
            </Button>
          )}
          <div>{labels.page}</div>
          <PageInput
            value={inputValue}
            total={totalPages}
            activePage={activePage}
            onPageChange={handlePageChange}
            setInputValue={setInputValue}
            labels={labels}
          />
          <div>{labels.ofTotalPages(totalPages)}</div>
          {showControls && (
            <Button
              variant="light"
              type="button"
              aria-label={labels.nextPage}
              onClick={handleNext}
              isIconOnly
              disabled={activePage === totalPages}
            >
              <AltArrowRightLinear className="size-4 rotate-90 text-icon-neutral-onMatch-usual md:size-6" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  const hasResultsCounter = showResultsCounter && totalItems;
  const placement = hasResultsCounter ? "justify-between" : "justify-end";

  return (
    <div className={cn("flex w-full flex-row items-center", placement, "text-text-body-neutral-onMatch-muted")}>
      {hasResultsCounter && (
        <div className="flex-shrink-0">
          <p className="text-sm text-text-body-neutral-onMatch-faint">
            {labels.resultsCounter(
              (activePage - 1) * perPage + 1,
              Math.min(activePage * perPage, totalItems),
              totalItems,
            )}
          </p>
        </div>
      )}
      <div className={cn("flex flex-shrink-0 flex-row items-center", className)}>
        {showControls && (
          <Button
            variant="light"
            isIconOnly
            aria-label={labels.previousPage}
            onClick={handlePrevious}
            disabled={activePage === 1}
          >
            <AltArrowLeftLinear className="size-5 text-icon-neutral-onMatch-usual md:size-6" />
          </Button>
        )}
        <PageInput
          value={inputValue}
          total={totalPages}
          activePage={activePage}
          onPageChange={handlePageChange}
          setInputValue={setInputValue}
          labels={labels}
        />
        <span className="pl-4 text-sm">{labels.ofTotalPages(totalPages)}</span>
        {showControls && (
          <Button
            variant="light"
            type="button"
            aria-label={labels.nextPage}
            onClick={handleNext}
            isIconOnly
            disabled={activePage === totalPages}
          >
            <AltArrowRightLinear className="size-5 text-icon-neutral-onMatch-usual md:size-6" />
          </Button>
        )}
      </div>
    </div>
  );
};
